import type { ActivityEvent } from '@fleequid-secrets/shared';
import { useInfiniteQuery } from '@tanstack/react-query';
import { type CSSProperties, type ReactNode, useMemo } from 'react';
import { api, type ActivityFilter, messageOf } from '@/api/client';
import { type ActivityKind, sentence } from '@/lib/activity';
import { dayKey, dayLabel, initials, romeTime, stamp } from '@/lib/format';
import { useNow } from '@/lib/hooks';
import { Link, setParam, useSearch } from '@/lib/router';
import { vaultPath } from '@/lib/routes';
import { useShell } from '@/lib/shell';

const KINDS: ReadonlyArray<{ kind: ActivityKind | null; label: string }> = [
    { kind: null, label: 'Everything' },
    { kind: 'changes', label: 'Changes' },
    { kind: 'reads', label: 'Reveals and copies' },
    { kind: 'access', label: 'Sign-ins and people' },
];

// ACTIVITY: every change, every reveal, copy and export, every sign-in, newest first, a day at a time. Values never
// appear: a reveal says who looked at which key, not what they saw.
export function Activity(): ReactNode {
    const { environments } = useShell();
    const search = useSearch();
    const now = useNow(60_000);
    const params = new URLSearchParams(search);
    const kind = (['changes', 'reads', 'access'] as const).find((item) => item === params.get('kind'));
    const environmentSlug = params.get('env');
    const environment = environments.find((item) => item.slug === environmentSlug);
    const filter: ActivityFilter = { kind, environment: environment?.id };
    const pages = useInfiniteQuery({
        queryKey: ['activity', filter],
        queryFn: ({ pageParam }) => api.activity({ ...filter, before: pageParam, limit: 60 }),
        initialPageParam: null as string | null,
        getNextPageParam: (page) => page.next,
    });
    const events = useMemo(() => pages.data?.pages.flatMap((page) => page.events) ?? [], [pages.data]);
    const days = useMemo(() => {
        const groups: Array<{ key: string; label: string; events: ActivityEvent[] }> = [];
        for (const event of events) {
            const key = dayKey(event.at);
            const last = groups.at(-1);
            if (last?.key === key) {
                last.events.push(event);
            } else {
                groups.push({ key, label: dayLabel(event.at, now), events: [event] });
            }
        }

        return groups;
    }, [events, now]);

    return (
        <main className="page page-scroll">
            <div className="page-head">
                <div>
                    <h1>Activity</h1>
                    <p>Who changed what, who looked at what, who signed in. Everyone on the team sees it; no value ever appears in it.</p>
                </div>
                <div className="side">
                    <select className="field" value={environment?.slug ?? ''} onChange={(event) => setParam('env', event.target.value || null)}>
                        <option value="">Every environment</option>
                        {environments.map((item) => (
                            <option key={item.id} value={item.slug}>
                                {item.name}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
            <div className="toolbar">
                <div className="seg">
                    {KINDS.map((item) => (
                        <button key={item.label} className={(kind ?? null) === item.kind ? 'on' : ''} onClick={() => setParam('kind', item.kind)}>
                            {item.label}
                        </button>
                    ))}
                </div>
            </div>
            <div className="panel">
                {pages.isError && <div className="empty">{messageOf(pages.error)}</div>}
                {pages.isSuccess && events.length === 0 && (
                    <div className="empty">
                        <b>Nothing yet</b>
                        What happens in the vault will show up here.
                    </div>
                )}
                {days.map((day) => (
                    <div key={day.key} className="day">
                        <h3>{day.label}</h3>
                        {day.events.map((event) => (
                            <EventRow key={event.id} event={event} />
                        ))}
                    </div>
                ))}
                {pages.hasNextPage && (
                    <div className="more">
                        <button className="act" onClick={() => void pages.fetchNextPage()} disabled={pages.isFetchingNextPage}>
                            {pages.isFetchingNextPage ? 'Loading…' : 'Older'}
                        </button>
                    </div>
                )}
            </div>
        </main>
    );
}

function EventRow(props: { event: ActivityEvent }): ReactNode {
    const { environments } = useShell();
    const { event } = props;
    const words = sentence(event);
    const environment = environments.find((item) => item.id === event.environment?.id);
    const section = environment?.sections.find((item) => item.id === event.section?.id);
    // Where it happened, unless the sentence already names it (a section created, an environment renamed).
    const aboutSection = Boolean(words.subject && !words.subject.code && words.subject.text === event.section?.name);
    const aboutEnvironment = Boolean(!event.section && words.subject && words.subject.text === event.environment?.name);
    const where =
        event.environment && !aboutEnvironment ? (
            <span className="where">
                <i className="dot" style={{ '--c': environment?.color ?? 'var(--ink-ghost)' } as CSSProperties} />
                {environment && section ? (
                    <Link to={vaultPath(environment.slug, section.slug, event.variable?.key)}>
                        {event.environment.name}
                        {event.section && !aboutSection ? ` · ${event.section.name}` : ''}
                    </Link>
                ) : (
                    <span>
                        {event.environment.name}
                        {event.section && !aboutSection ? ` · ${event.section.name}` : ''}
                    </span>
                )}
            </span>
        ) : null;

    return (
        <div className={`event ${words.kind} ${words.failed ? 'failed' : ''}`}>
            <span className="time" title={stamp(event.at)}>
                {romeTime(event.at)}
            </span>
            <span className="avatar small">{initials(words.who)}</span>
            <span className="sentence">
                <b>{words.who}</b> {words.verb}{' '}
                {words.subject && (words.subject.code ? <code>{words.subject.text}</code> : <b>{words.subject.text}</b>)}
                {words.extra && <> · {words.extra}</>}
                {where}
            </span>
            <span className="ip">{event.ip ?? ''}</span>
        </div>
    );
}
